import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ROLES = [
  { label: 'System Admin', role: 'admin', email: 'admin@ariaforge.com', password: 'Admin@2024!', color: 'hsl(197,100%,48%)' },
  { label: 'Factory Mgr', role: 'factory_manager', email: 'factory@ariaforge.com', password: 'Test@2024!', color: 'hsl(12,90%,55%)' },
  { label: 'Quality Mgr', role: 'quality_manager', email: 'quality@ariaforge.com', password: 'Test@2024!', color: 'hsl(43,96%,56%)' },
  { label: 'Sales Mgr', role: 'sales_manager', email: 'sales@ariaforge.com', password: 'Test@2024!', color: 'hsl(280,65%,60%)' },
  { label: 'Dispatch', role: 'dispatch_user', email: 'dispatch@ariaforge.com', password: 'Test@2024!', color: 'hsl(140,60%,45%)' },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeRole, setActiveRole] = useState(null);

  const handleRoleClick = (role) => {
    setEmail(role.email);
    setPassword(role.password);
    setActiveRole(role.role);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'hsl(0,0%,3%)', display: 'flex', alignItems: 'stretch' }}>
      {/* Left panel — brand + tagline */}
      <div style={{
        flex: 1, position: 'relative', overflow: 'hidden', display: 'none',
        backgroundImage: `linear-gradient(135deg, hsl(197,100%,8%) 0%, hsl(0,0%,5%) 50%, hsl(0,0%,3%) 100%)`,
      }} className="login-left">
        {/* Geometric mesh background */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.06,
          backgroundImage: `repeating-linear-gradient(0deg, hsl(197,100%,48%) 0px, transparent 1px, transparent 60px),
            repeating-linear-gradient(90deg, hsl(197,100%,48%) 0px, transparent 1px, transparent 60px)`,
        }} />
        <div style={{ position: 'relative', zIndex: 1, padding: 48, height: '100%',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 36, fontWeight: 700,
              color: 'hsl(197,100%,48%)', letterSpacing: '0.1em', textTransform: 'uppercase',
              animation: 'fadeInUp 0.6s ease forwards' }}>
              ARIA FORGE
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,45%)',
              textTransform: 'uppercase', letterSpacing: '0.3em', marginTop: 4 }}>
              ERP Platform · Manufacturing OS
            </div>
          </div>
          <div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 28, fontWeight: 600,
              color: 'hsl(0,0%,90%)', lineHeight: 1.3, maxWidth: 420,
              animation: 'fadeInUp 0.6s ease 0.2s forwards', opacity: 0 }}>
              The Command Center for Indian OEM Manufacturing
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 32,
              animation: 'fadeInUp 0.6s ease 0.4s forwards', opacity: 0 }}>
              {['Customer Orders', 'Production Control', 'Quality Mgmt', 'Dispatch & Compliance'].map(tag => (
                <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 6, height: 6, background: 'hsl(197,100%,48%)' }} />
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    color: 'hsl(0,0%,55%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                    {tag}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div style={{
        width: '100%', maxWidth: 460, background: 'hsl(0,0%,4%)', borderLeft: '1px solid hsl(0,0%,12%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 40px',
        margin: '0 auto',
      }} className="login-right">
        <div style={{ width: '100%', animation: 'fadeIn 0.4s ease 0.1s forwards', opacity: 0 }}>
          {/* Mobile logo */}
          <div style={{ marginBottom: 28, textAlign: 'center' }}>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 28, fontWeight: 700,
              color: 'hsl(197,100%,48%)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 2 }}>
              ARIA FORGE
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)',
              textTransform: 'uppercase', letterSpacing: '0.25em' }}>
              Manufacturing ERP
            </div>
          </div>

          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
              color: 'hsl(0,0%,98%)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Sign In
            </h1>
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,45%)',
              textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Access your workspace
            </p>
          </div>

          {error && (
            <div data-testid="login-error" style={{
              background: 'hsl(0,84%,10%)', border: '1px solid hsl(0,84%,35%)',
              padding: '10px 14px', marginBottom: 16, fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, color: 'hsl(0,84%,70%)'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 }}>
                Email
              </label>
              <input data-testid="login-email" type="email" value={email}
                onChange={e => { setEmail(e.target.value); setActiveRole(null); }}
                required placeholder="admin@ariaforge.com"
                style={{ width: '100%', background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
                  padding: '11px 14px', color: 'hsl(0,0%,90%)', fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                onFocus={e => e.target.style.borderColor = 'hsl(197,100%,48%)'}
                onBlur={e => e.target.style.borderColor = 'hsl(0,0%,18%)'} />
            </div>
            <div style={{ marginBottom: 22 }}>
              <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 }}>
                Password
              </label>
              <input data-testid="login-password" type="password" value={password}
                onChange={e => setPassword(e.target.value)} required placeholder="••••••••"
                style={{ width: '100%', background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
                  padding: '11px 14px', color: 'hsl(0,0%,90%)', fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                onFocus={e => e.target.style.borderColor = 'hsl(197,100%,48%)'}
                onBlur={e => e.target.style.borderColor = 'hsl(0,0%,18%)'} />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="btn-primary" style={{ width: '100%', padding: '13px', fontSize: 14,
                opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </button>
          </form>

          {/* Role Quick-Access Buttons */}
          <div style={{ marginTop: 24, background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 16 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)',
              textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 12 }}>
              Demo Access — Click to populate
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ROLES.map(r => (
                <button key={r.role} data-testid={`demo-role-${r.role}`}
                  onClick={() => handleRoleClick(r)}
                  style={{
                    padding: '6px 12px', cursor: 'pointer', border: '1px solid',
                    borderColor: activeRole === r.role ? r.color : 'hsl(0,0%,20%)',
                    background: activeRole === r.role ? `${r.color}22` : 'hsl(0,0%,8%)',
                    color: activeRole === r.role ? r.color : 'hsl(0,0%,55%)',
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => {
                    if (activeRole !== r.role) {
                      e.currentTarget.style.borderColor = r.color;
                      e.currentTarget.style.color = r.color;
                    }
                  }}
                  onMouseLeave={e => {
                    if (activeRole !== r.role) {
                      e.currentTarget.style.borderColor = 'hsl(0,0%,20%)';
                      e.currentTarget.style.color = 'hsl(0,0%,55%)';
                    }
                  }}>
                  {r.label}
                </button>
              ))}
            </div>
            {activeRole && (
              <div style={{ marginTop: 10, fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                color: 'hsl(0,0%,45%)', borderTop: '1px solid hsl(0,0%,10%)', paddingTop: 8 }}>
                <span style={{ color: ROLES.find(r => r.role === activeRole)?.color }}>
                  {ROLES.find(r => r.role === activeRole)?.email}
                </span>
                {' '}populated — click Sign In
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (min-width: 769px) {
          .login-left { display: flex !important; }
          .login-right { max-width: 440px !important; margin: 0 !important; }
        }
      `}</style>
    </div>
  );
}
